import { CanonicalActions } from "./canonical_actions.js";
import { formLabel } from "./techpack_form_contract.js";
import { useEffect, useState } from "react";
import { TechPackDoc, type FieldEnvelope, serializeTechPackCollections } from "../schema.js";
import { useStudio, useYMap } from "./StudioContext.js";
import { getFreshToken } from "./auth.js";
import {
  BTN, STATUS_CYCLE, STATUS_NEXT, STATUS_STYLE, ui,
  type Lang, type TechPackEntry, type DocStatus,
} from "./shared.js";

// ---------------------------------------------------------------------------
// PDF export helper — serializes Yjs state to plain JSON for the API
// ---------------------------------------------------------------------------

export function collectTechPackPayload(techPack: TechPackDoc, lang: string): Record<string, unknown> {
  function fv(ymap: { get(k: string): FieldEnvelope | undefined }, key: string): string {
    return String(ymap.get(key)?.value ?? "");
  }
  const meta = techPack.metadata;
  const cd   = techPack.construction_details;
  const meas: Record<string, string> = {};
  for (const [k, v] of techPack.measurements.entries()) {
    meas[k] = String(v?.value ?? "");
  }
  return {
    lang,
    style_number:     fv(meta, "style_number"),
    project_name:     fv(meta, "project_name"),
    product_category: fv(meta, "product_category"),
    season:           fv(meta, "season"),
    version:          fv(meta, "version"),
    doc_status:       fv(meta, "_doc_status"),
    size_range:       fv(meta, "size_range"),
    size_system:      fv(meta, "size_system"),
    description:      fv(meta, "description"),
    design_sketch:    techPack.images.get("design_sketch") ?? "",
    bom: techPack.bom.toArray().map(r => ({ ...r, swatch: techPack.images.get(`bom_swatch_${r.id}`) ?? "" })),
    interlining: techPack.interlining.toArray().map(r => ({ ...r, swatch: techPack.images.get(`interlining_swatch_${r.id}`) ?? "" })),
    measurements: meas,
    pattern_pieces: techPack.pattern_pieces.toArray(),
    construction_instructions: techPack.construction_instructions.toArray(),
    construction_details: {
      seam_allowance:     fv(cd, "seam_allowance"),
      tolerance:          fv(cd, "tolerance"),
      production_type:    fv(cd, "production_type"),
      special_techniques: fv(cd, "special_techniques"),
      hardware_trim:      fv(cd, "hardware_trim"),
      additional_notes:   fv(cd, "additional_notes"),
    },
  };
}

// ---------------------------------------------------------------------------
// EditorHeader
// ---------------------------------------------------------------------------

interface EditorHeaderProps {
  pack: TechPackEntry;
  lang: Lang;
  onBack: () => void;
  onStatusChange: (s: DocStatus) => void;
  onExportCredit: () => void;
  onSubmit: () => void;
}

export function EditorHeader({ pack, lang, onBack, onStatusChange, onExportCredit, onSubmit }: EditorHeaderProps) {
  const { techPack, userId, roomId } = useStudio();
  const meta = useYMap(techPack.metadata);
  const rawStatus = (meta.get("_doc_status") as FieldEnvelope|undefined)?.value as string|undefined;
  const docStatus: DocStatus = ((Object.keys(STATUS_STYLE) as DocStatus[]).includes(rawStatus?.toLowerCase() as DocStatus)
    ? rawStatus!.toLowerCase() : 'draft') as DocStatus;
  const sc = STATUS_STYLE[docStatus];
  const styleNum = (meta.get("style_number") as FieldEnvelope|undefined)?.value as string|undefined;
  const [exporting,    setExporting]    = useState(false);
  const [saving,       setSaving]       = useState(false);
  const [saveStatus,   setSaveStatus]   = useState<"idle" | "saved" | "error">("idle");
  // Autosave status — driven by studio:save-status events from App.autoSave.
  // Gives the founder the same visible confirmation as the manual Save button.
  const [autoStatus,   setAutoStatus]   = useState<"idle" | "saving" | "saved" | "error">("idle");

  useEffect(() => {
    function onStatus(e: Event) {
      const s = (e as CustomEvent<{ status: string }>).detail?.status;
      if (s === "saving") {
        setAutoStatus("saving");
      } else if (s === "saved") {
        setAutoStatus("saved");
        setTimeout(() => setAutoStatus("idle"), 2000);
      } else if (s === "error") {
        setAutoStatus("error");
        setTimeout(() => setAutoStatus("idle"), 4000);
      }
    }
    window.addEventListener("studio:save-status", onStatus);
    return () => window.removeEventListener("studio:save-status", onStatus);
  }, []);

  function advanceStatus() {
    const next = STATUS_NEXT[docStatus];
    techPack.ydoc.transact(() => { techPack.metadata.set("_doc_status", { value: next, language_code: "en", last_edited_by: userId, timestamp: Date.now() }); });
    onStatusChange(next);
  }

  async function handleSave() {
    if (!roomId) { console.warn("[save] roomId not ready"); return; }
    if (saving) return;
    setSaving(true);
    setSaveStatus("idle");
    try {
      const token = await getFreshToken();
      const payload = {
        techpackId:               roomId,
        userId,
        // G1: explicit Save uses the SAME canonical serializer as autosave so the
        // two save paths can never diverge on which collections they persist.
        ...serializeTechPackCollections(techPack),
        activities:               techPack.ydoc.getArray("__studio_activity__").toArray(),
        chat_messages:            techPack.ydoc.getArray("__studio_chat__").toArray(),
      };
      // ?activity=save → server logs ONE explicit-save Activity event (autosave,
      // which omits the flag, logs nothing). Meaningful action vs persistence.
      const res = await fetch(`/api/studio/techpacks/${roomId}?activity=save`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        setSaveStatus("saved");
        window.dispatchEvent(new CustomEvent("techpack-saved", {
          detail: { id: roomId, styleNumber: (techPack.metadata.get("style_number") as FieldEnvelope | undefined)?.value ?? pack.styleNumber }
        }));
        setTimeout(() => setSaveStatus("idle"), 2000);
      } else {
        console.error("[save] failed:", res.status, await res.text());
        setSaveStatus("error");
        setTimeout(() => setSaveStatus("idle"), 3000);
      }
    } catch (err) {
      console.error("[save] error:", err);
      setSaveStatus("error");
      setTimeout(() => setSaveStatus("idle"), 3000);
    } finally {
      setSaving(false);
    }
  }

  // XLSX export — kept for internal use; not wired to a visible button
  async function doExport() {
    if (exporting) return;
    setExporting(true);
    try {
      const tp = collectTechPackPayload(techPack, lang);
      const metaKeys = ["style_number","project_name","product_category","season","version","doc_status","size_range","size_system","description"] as const;
      const rows: string[][] = [["Field", "Value"]];
      for (const k of metaKeys) { const v = tp[k] as string; if (v) rows.push([k, v]); }
      const bomArr = tp.bom as Array<Record<string, string>>;
      if (bomArr.length) {
        rows.push([], ["--- BOM ---"]);
        rows.push(Object.keys(bomArr[0]));
        for (const row of bomArr) rows.push(Object.values(row));
      }
      const token = await getFreshToken() ?? "";
      const res = await fetch("/api/export/xlsx", {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ rows, sheetName: styleNum || "TechPack" }),
      });
      if (!res.ok) throw new Error(`Server ${res.status}`);
      const buf = await res.arrayBuffer();
      const blob = new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `STUDIO_${(styleNum || "EXPORT").replace(/[^A-Za-z0-9\-_]/g,"_")}.xlsx`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 8000);
      onExportCredit();
    } catch (err) {
      console.error("[export] XLSX generation failed:", err);
    } finally {
      setExporting(false);
    }
  }

  return (
    <div className="sf-editor-toolbar" style={{ display:"flex", alignItems:"center", gap:10, padding:"8px 16px", minHeight:44, flexWrap:"wrap", flexShrink:0, borderBottom:"1px solid var(--border)", background:"#2d2d2d", color:"#ededed" }}>
      <button onClick={onBack} style={{ ...BTN, color:"#ededed", fontSize:11, padding:"6px 12px", letterSpacing:0 }}>{ui("back", lang)}</button>
      <span style={{ fontFamily:"'Space Mono', monospace", fontSize:12, fontWeight:700, letterSpacing:"0.05em", color:"var(--text)", flex:1, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{styleNum || pack.styleNumber}</span>
      <button onClick={advanceStatus} style={{ ...BTN, borderColor:sc.border, background:sc.bg, color:"#ededed" }}>{formLabel(docStatus,lang)}</button>
      {autoStatus !== "idle" && (
        <span style={{ fontFamily:"'Space Mono', monospace", fontSize:11, letterSpacing:"0.05em", whiteSpace:"nowrap", color: autoStatus === "error" ? "#f44336" : autoStatus === "saved" ? "#4caf50" : "var(--text-muted)" }}>
          {autoStatus === "saving" ? ui("saving", lang) : autoStatus === "saved" ? `✓ ${ui("saved", lang)}` : ui("save_failed", lang)}
        </span>
      )}
      <button onClick={() => { void handleSave(); }} disabled={saving} style={{ ...BTN, opacity: saving ? 0.5 : 1, ...(saveStatus === "saved" ? { borderColor:"#4caf50" } : saveStatus === "error" ? { borderColor:"#f44336" } : {}), color:saveStatus === "saved" ? "#4caf50" : saveStatus === "error" ? "#f44336" : "#ededed" }}>{saving ? ui("saving", lang) : saveStatus === "saved" ? `✓ ${ui("save", lang)}` : ui("save", lang)}</button>
      <CanonicalActions />
      <button className="sf-editor-submit" onClick={onSubmit} style={{ ...BTN, background:"#fff", borderColor:"#fff", color:"#000" }}>{ui("submit", lang)}</button>
    </div>
  );
}
